'use client';

import { createTheme } from '@mui/material/styles';
import { SHORT_LANDSCAPE } from './shortLandscape';

// Tells MUI's types that the theme uses CSS variables, which unlocks the matching ThemeProvider props.
declare module '@mui/material/styles' {
  interface CssThemeVariables {
    enabled: true;
  }
}

const sharedOptions = {
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiDialog: {
      defaultProps: {
        disableEnforceFocus: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    // The floating Timmy button sits in the bottom right corner below md, so the pagination keeps clear of it. Phones
    // also move it left and drop the "Rows per page" label. A phone held sideways gets a thinner row to leave the list room.
    MuiTablePagination: {
      styleOverrides: {
        root: {
          '@media (max-width: 899.95px)': {
            '& .MuiTablePagination-toolbar': { paddingRight: 72 },
          },
          [SHORT_LANDSCAPE]: {
            '& .MuiTablePagination-toolbar': { minHeight: 40 },
          },
          '@media (max-width: 599.95px)': {
            '& .MuiTablePagination-toolbar': { paddingLeft: 4 },
            '& .MuiTablePagination-spacer': { display: 'none' },
            '& .MuiTablePagination-selectLabel': { display: 'none' },
            '& .MuiTablePagination-input': { marginLeft: 0, marginRight: 12 },
          },
        },
      },
    },
  },
} as const;

// Both palettes ship as CSS variables, and `data-theme` on <html> picks one. The server renders
// the same HTML for everyone, so pages can be static, and THEME_BOOT_SCRIPT sets the attribute
// before the first paint so dark mode never flashes light.
const theme = createTheme({
  ...sharedOptions,
  cssVariables: { colorSchemeSelector: 'data-theme' },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: '#000000',
          light: '#333333',
          dark: '#000000',
          contrastText: '#ffffff',
        },
        secondary: {
          main: '#f44336',
        },
        background: {
          default: '#f5f5f5',
          paper: '#ffffff',
        },
      },
    },
    dark: {
      palette: {
        primary: {
          main: '#f5f5f5',
          light: '#ffffff',
          dark: '#d4d4d4',
          contrastText: '#111111',
        },
        secondary: {
          main: '#f44336',
        },
        // .dark is what status labels use. On a dark surface it has to be a light shade.
        success: { main: '#66bb6a', light: '#a5d6a7', dark: '#81c784' },
        warning: { main: '#ffa726', light: '#ffcc80', dark: '#ffb74d' },
        error: { main: '#ef5350', light: '#ef9a9a', dark: '#ffcdd2' },
        info: { main: '#29b6f6', light: '#81d4fa', dark: '#4fc3f7' },
        // 50–400 are fills and stay lighter than paper. 900 is the dark header fill.
        grey: {
          50: '#3c3c3c',
          100: '#454545',
          200: '#4e4e4e',
          300: '#5a5a5a',
          400: '#707070',
          900: '#3a3a3a',
        },
        background: {
          default: '#242424',
          paper: '#303030',
        },
      },
    },
  },
});

export default theme;
